import { NgModule } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  MsalInterceptorConfiguration, // <-- added
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '@env/environment';
import { OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

import { AuthGuard } from './guards/auth.guard';
import { PreAuthGuard } from './guards/pre-auth.guard';
import { AUTHENTICATION_CONFIGURATION_NAMES } from './services/auth/authenticationConfigNames';

// NOTE: AuthInterceptor is now provided in app.config.ts (do not provide it here)
// NOTE: AzureAuthAllowedOrgInterceptor has been retired (replaced by context-aware interceptors)

let providers: Array<any> = [
  PreAuthGuard,
  AuthGuard,
];
const imports: Array<any> = [];
const bootstrapComponents: Array<any> = [];

const authSetup = environment.auth.reduce(
  (a, v) => ({ ...a, [v.authConfigName]: v }),
  {},
);

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA].clientId,
      authority: 'https://login.microsoft.com/organizations',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback: () => {},
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap: new Map<string, Array<string> | null>([
      ['https://graph.microsoft.com/v1.0/me', ['https://graph.microsoft.com/.default']],
      ['http://localhost:4300/*', null],
      [
        environment.serverUrl + '/*',
        [authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA].clientId + '/.default'],
      ],
    ]),
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { interactionType: InteractionType.Popup };
}

const msalProviders: Array<any> = [
  { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
  { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
  { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
  MsalService,
  MsalGuard,
  MsalBroadcastService,
];

if (
  authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA] &&
  authSetup[AUTHENTICATION_CONFIGURATION_NAMES.ENTRA].enabled
) {
  imports.push(MsalModule);
  providers = providers.concat(...msalProviders);
  bootstrapComponents.push(MsalRedirectComponent);
}

if (
  authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA] &&
  authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA].enabled
) {
  const oktaAuth =
    window.location.search &&
    window.location.search.includes('login=esqep') &&
    authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA].sqeptechOpenIdIssuer
      ? new OktaAuth({
          issuer: authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA].sqeptechOpenIdIssuer,
          clientId: authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA].sqeptechClientId,
          redirectUri: window.location.origin + '/login/callback',
        })
      : new OktaAuth({
          issuer: authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA].openIdIssuer,
          clientId: authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA].clientId,
          redirectUri: window.location.origin + '/login/callback',
        });
  const moduleConfig: OktaConfig = { oktaAuth };
  imports.push(OktaAuthModule.forRoot(moduleConfig));
}

@NgModule({
  declarations: [],
  imports: imports,
  providers: providers,
  bootstrap: bootstrapComponents,
})
export class AuthModule {}